"use client";

import { Combobox } from "@/app/_components/ui/combobox";
import { useTRPC } from "@/trpc/client";
import { State } from "@prisma/client";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useState } from "react";

type SchoolOption = { id: number; name: string };

export function SchoolSelect({
  state,
  value,
  onChange,
  selectedLabel: initialSelectedLabel,
}: {
  state?: State;
  value?: number;
  onChange: (schoolId: number | undefined) => void;
  selectedLabel?: string;
}) {
  const trpc = useTRPC();
  const [searchTerm, setSearchTerm] = useState<string>("");
  // Cached independently of `loaded`: the selected school can page out of
  // the current search results, but its label should still show. Prefill
  // supplies the initial label so a school not in the first page still
  // renders its name.
  const [selectedLabel, setSelectedLabel] = useState<string | undefined>(
    initialSelectedLabel,
  );

  // Reset the search and cached label when the state filter changes, rather
  // than in an effect: setState during render for a prop-driven reset is the
  // pattern React recommends (and `react-hooks/set-state-in-effect` forbids
  // doing this in a useEffect).
  const [lastState, setLastState] = useState(state);
  if (state !== lastState) {
    setLastState(state);
    setSearchTerm("");
    setSelectedLabel(undefined);
  }

  const schoolsQuery = useInfiniteQuery(
    trpc.schools.search.infiniteQueryOptions(
      {
        filters: { state: state ?? State.NY },
        sortOrder: "nameAsc",
        searchTerm: searchTerm || undefined,
      },
      {
        enabled: state != null,
        initialCursor: 1,
        getNextPageParam: (lastPage, pages) => {
          const loadedCount = pages.reduce(
            (sum, page) => sum + page.schools.length,
            0,
          );
          return loadedCount < lastPage.totalCount
            ? pages.length + 1
            : undefined;
        },
      },
    ),
  );

  const loaded: SchoolOption[] =
    schoolsQuery.data?.pages.flatMap((page) =>
      page.schools.map((school) => ({ id: school.id, name: school.name })),
    ) ?? [];
  const selected = loaded.find((school) => school.id === value);

  return (
    <Combobox
      className="min-w-0"
      menuTheme="lavender"
      value={value != null ? String(value) : undefined}
      selectedValueOverride={selected?.name ?? selectedLabel}
      items={loaded.map((school) => ({
        value: String(school.id),
        label: school.name,
      }))}
      onSelect={(next) => {
        const nextId = next ? Number(next) : undefined;
        setSelectedLabel(
          nextId != null
            ? loaded.find((school) => school.id === nextId)?.name
            : undefined,
        );
        setSearchTerm("");
        onChange(nextId);
      }}
      onRemoveSelectedClick={
        value != null
          ? () => {
              setSelectedLabel(undefined);
              onChange(undefined);
            }
          : undefined
      }
      selectItemMsg={state ? "School name" : "Select a state first"}
      searchPlaceholder="Search schools"
      onSearchChange={async (term) => {
        setSearchTerm(term);
      }}
      noResultsMsg={
        schoolsQuery.isError ? "Couldn't load schools" : "No schools found"
      }
      disabled={!state}
      loading={state != null && schoolsQuery.isLoading}
      noMoreResults={!schoolsQuery.hasNextPage}
      onScrollBottom={async () => {
        if (schoolsQuery.hasNextPage && !schoolsQuery.isFetchingNextPage) {
          await schoolsQuery.fetchNextPage();
        }
      }}
      muteSelectItemText
    />
  );
}
